import React from 'react';
import { Compass } from 'lucide-react';
import { saveSelectionToSupabase } from '@/lib/supabase';
import PageLayout from '@/components/PageLayout';
import AdBanner from '@/components/AdBanner';
import logo from '/lovable-uploads/40a4ee59-7714-4091-a6f7-e5ba5819fe5f.png';
import { useTranslation } from 'react-i18next';
import { useOnboardingContext } from '@/context/OnboardingContext';

const Data = () => {
  const { t } = useTranslation();
  const { userData } = useOnboardingContext();

  const handleDataSelect = async (dataName: string, price: string, telNumber: string) => {
    // Create selection object
    const selection = {
      type: 'Data',
      name: dataName,
      price: price,
      dateTime: new Date().toISOString(),
    };
    
    // Save to localStorage
    try {
      const existingSelections = JSON.parse(localStorage.getItem('selections') || '[]');
      existingSelections.push(selection);
      localStorage.setItem('selections', JSON.stringify(existingSelections));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
    
    // Save to Supabase
    try {
      await saveSelectionToSupabase(selection, userData?.email);
    } catch (error) {
      console.error('Error in Supabase save:', error);
    }
    
    // Redirect to phone dialer
    window.location.href = telNumber;
  };

  return (
    <PageLayout 
      title={t('home.services.data')} 
      logo={logo}
      showBackButton
      colorScheme="bg-gradient-to-r from-orange-500 to-amber-500"
    >
      {/* Ad Banner for data page */}
      <AdBanner pageSlug="data" />
      
      <div className="space-y-2 rounded-xl overflow-hidden shadow-lg">
        {/* Express Plan */}
        <div 
          className="bg-white border-b cursor-pointer hover:bg-orange-50 transition-colors"
          onClick={() => handleDataSelect("Express", "200", "tel:*164#2*1*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 w-16 h-16 overflow-hidden rounded-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 shadow-md">
              <div className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16.2 3.8a2.7 2.7 0 0 0-3.81 0l-.4.38-.4-.4a2.7 2.7 0 0 0-3.82 0C6.73 4.85 6.67 6.64 8 8l4 4 4-4c1.33-1.36 1.27-3.15.2-4.2z" />
                  <path d="M8 8c-1.36-1.33-3.15-1.27-4.2-.2a2.7 2.7 0 0 0 0 3.81l.38.4-.4.4a2.7 2.7 0 0 0 0 3.82C4.85 17.27 6.64 17.33 8 16" />
                  <path d="M16 16c1.36 1.33 3.15 1.27 4.2.2a2.7 2.7 0 0 0 0-3.81l-.38-.4.4-.4a2.7 2.7 0 0 0 0-3.82C19.15 6.73 17.36 6.67 16 8" />
                  <path d="m7.8 20.2-.4-.38-.4.4a2.7 2.7 0 0 1-3.82 0c-1.06-1.06-1.12-2.85.2-4.2 1.36 1.33 3.15 1.27 4.2.2l.4-.38.4.4a2.7 2.7 0 0 0 3.82 0c1.06-1.06 1.12-2.85-.2-4.2" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Express</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">200</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('data.express.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('data.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">1</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Découverte Plan */}
        <div 
          className="bg-white border-b cursor-pointer hover:bg-orange-50 transition-colors"
          onClick={() => handleDataSelect("Découverte", "500", "tel:*164#2*2*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 w-16 h-16 overflow-hidden rounded-full flex items-center justify-center bg-gradient-to-br from-green-400 to-green-600 shadow-md">
              <div className="text-white">
                <Compass size={40} />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Découverte</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">500</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('data.decouverte.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('data.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">3</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Evasion Plan */}
        <div 
          className="bg-white border-b cursor-pointer"
          onClick={() => handleDataSelect("Evasion", "1000", "tel:*164#2*3*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 w-16 h-16 overflow-hidden rounded-full flex items-center justify-center bg-[#FFD700]">
              <div className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" fill="#FFD700" stroke="#FFD700" />
                  <circle cx="8" cy="8" r="2" fill="#2196F3" stroke="#2196F3" />
                  <circle cx="16" cy="8" r="2" fill="#2196F3" stroke="#2196F3" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="#2196F3" strokeWidth="1" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Evasion</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">1000</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('data.evasion.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('data.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">7</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Confort+ Plan */}
        <div 
          className="bg-white border-b cursor-pointer"
          onClick={() => handleDataSelect("Confort+", "3000", "tel:*164#2*4*1#")}
        >
          <div className="flex items-start p-4">
            <div className="mr-4 w-16 h-16 overflow-hidden rounded-full flex items-center justify-center bg-[#E91E63]">
              <div className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="white" stroke="white" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-medium text-gray-700">Confort+</h2>
                <div className="flex items-end">
                  <span className="text-4xl font-bold text-gray-700">3000</span>
                  <span className="text-lg ml-1 text-gray-500">DJF</span>
                </div>
              </div>
              <p className="text-gray-500 mt-1">{t('data.confort.description')}</p>
              <div className="flex items-center mt-2">
                <span className="text-gray-500">{t('data.validity')}</span>
                <span className="mx-4 text-2xl font-medium text-gray-700">30</span>
                <span className="text-gray-500">{t('data.days')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Data;
